import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Plus, Send, Trash2, ExternalLink, Car } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const statusColors = { draft: "bg-neutral-100 text-neutral-700", sent: "bg-blue-100 text-blue-800", viewed: "bg-purple-100 text-purple-800", accepted: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800" };

export default function AdminOffers() {
    const [offers, setOffers] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({
        contact_id: "", vehicle_model: "", vat_mode: "ink",
        items: [{ description: "", price: 0 }],
        leasing_options: [],
        notes: ""
    });
    const [sending, setSending] = useState(false);

    const fetchOffers = () => { api.get("/offers").then((r) => setOffers(r.data)).catch(console.error).finally(() => setLoading(false)); };
    const fetchContacts = () => { api.get("/contacts").then((r) => setContacts(r.data)).catch(console.error); };
    useEffect(() => { fetchOffers(); fetchContacts(); }, []);

    const addItem = () => setForm({ ...form, items: [...form.items, { description: "", price: 0 }] });
    const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
    const updateItem = (i, field, value) => { const items = [...form.items]; items[i] = { ...items[i], [field]: field === "price" ? parseFloat(value) || 0 : value }; setForm({ ...form, items }); };

    const addLeasing = () => setForm({ ...form, leasing_options: [...form.leasing_options, { months: 36, residual_value: 0, monthly_cost: 0 }] });
    const removeLeasing = (i) => setForm({ ...form, leasing_options: form.leasing_options.filter((_, idx) => idx !== i) });
    const updateLeasing = (i, field, value) => { const opts = [...form.leasing_options]; opts[i] = { ...opts[i], [field]: parseFloat(value) || 0 }; setForm({ ...form, leasing_options: opts }); };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post("/offers", form);
            toast.success("Offert skapad");
            setShowCreate(false);
            setForm({ contact_id: "", vehicle_model: "", vat_mode: "ink", items: [{ description: "", price: 0 }], leasing_options: [], notes: "" });
            fetchOffers();
        } catch (err) { toast.error(err.response?.data?.detail || "Misslyckades"); }
    };

    const handleSend = async (offerId) => {
        setSending(true);
        try { await api.post(`/offers/${offerId}/send`); toast.success("Offert skickad"); fetchOffers(); }
        catch { toast.error("Misslyckades"); } finally { setSending(false); }
    };

    const total = form.items.reduce((s, i) => s + (i.price || 0), 0);

    return (
        <div className="p-8" data-testid="admin-offers">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-medium tracking-tight">Offerter</h1>
                <Button data-testid="create-offer-btn" onClick={() => setShowCreate(true)} className="bg-black text-white hover:bg-neutral-800">
                    <Plus size={16} className="mr-2" /> Ny offert
                </Button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                <Table>
                    <TableHeader><TableRow>
                        <TableHead className="text-xs uppercase tracking-wider">Kontakt</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider">Fordon</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider">Belopp</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider">Datum</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-right">Åtgärd</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                        {offers.map((o) => (
                            <TableRow key={o.id} data-testid={`offer-row-${o.id}`}>
                                <TableCell><div className="text-sm font-medium">{o.contact_name}</div><div className="text-xs text-neutral-400">{o.contact_email}</div></TableCell>
                                <TableCell className="text-sm">{o.vehicle_model ? `Birò ${o.vehicle_model === "small" ? "Small" : "Big"}` : "–"}</TableCell>
                                <TableCell className="text-sm font-medium">{o.total?.toLocaleString()} SEK {o.vat_mode === "ex" ? "(ex moms)" : "(ink moms)"}</TableCell>
                                <TableCell><Badge className={`text-[10px] ${statusColors[o.status] || "bg-neutral-100"}`}>{o.status}</Badge></TableCell>
                                <TableCell className="text-sm text-neutral-500">{o.created_at?.split("T")[0]}</TableCell>
                                <TableCell className="text-right flex gap-1 justify-end">
                                    {o.status === "draft" && <Button variant="ghost" size="sm" onClick={() => handleSend(o.id)} disabled={sending} data-testid={`send-offer-${o.id}`}><Send size={14} /></Button>}
                                    <a href={`/offers/${o.id}`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm"><ExternalLink size={14} /></Button></a>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!offers.length && <TableRow><TableCell colSpan={6} className="text-center text-neutral-400 py-8">Inga offerter</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </div>

            {/* Create Offer Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-offer-dialog">
                    <DialogHeader><DialogTitle>Ny offert</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-5">
                        {/* Contact & Vehicle */}
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs uppercase tracking-wider">Kontakt</Label>
                                <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
                                    <SelectTrigger className="mt-1" data-testid="offer-contact-select"><SelectValue placeholder="Välj kontakt" /></SelectTrigger>
                                    <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}</SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Fordon</Label>
                                <Select value={form.vehicle_model} onValueChange={(v) => setForm({ ...form, vehicle_model: v })}>
                                    <SelectTrigger className="mt-1" data-testid="offer-vehicle"><SelectValue placeholder="Välj modell" /></SelectTrigger>
                                    <SelectContent><SelectItem value="small">Birò Small</SelectItem><SelectItem value="big">Birò Big</SelectItem></SelectContent>
                                </Select></div>
                        </div>

                        {/* VAT mode */}
                        <div><Label className="text-xs uppercase tracking-wider">Moms</Label>
                            <Select value={form.vat_mode} onValueChange={(v) => setForm({ ...form, vat_mode: v })}>
                                <SelectTrigger className="mt-1 w-48" data-testid="offer-vat"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="ink">Inklusive moms</SelectItem><SelectItem value="ex">Exklusive moms</SelectItem></SelectContent>
                            </Select></div>

                        <Separator />

                        {/* Line Items */}
                        <div>
                            <Label className="text-xs uppercase tracking-wider mb-2 block">Poster</Label>
                            {form.items.map((item, i) => (
                                <div key={i} className="flex gap-2 mb-2">
                                    <Input data-testid={`offer-item-desc-${i}`} value={item.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder="Beskrivning" className="flex-1" />
                                    <Input data-testid={`offer-item-price-${i}`} type="number" value={item.price} onChange={(e) => updateItem(i, "price", e.target.value)} placeholder="Pris (SEK)" className="w-32" />
                                    {form.items.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}><Trash2 size={14} /></Button>}
                                </div>
                            ))}
                            <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus size={14} className="mr-1" /> Lägg till rad</Button>
                        </div>

                        <Separator />

                        {/* Leasing Options */}
                        <div>
                            <Label className="text-xs uppercase tracking-wider mb-2 block">Leasingförslag (valfritt)</Label>
                            {form.leasing_options.map((lo, i) => (
                                <div key={i} className="border border-neutral-200 rounded-md p-3 mb-2 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-neutral-500">Leasingalternativ {i + 1}</span>
                                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLeasing(i)}><Trash2 size={12} /></Button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div><Label className="text-[10px] uppercase tracking-wider">Månader</Label>
                                            <Input data-testid={`leasing-months-${i}`} type="number" value={lo.months} onChange={(e) => updateLeasing(i, "months", e.target.value)} className="mt-1" /></div>
                                        <div><Label className="text-[10px] uppercase tracking-wider">Restvärde (SEK)</Label>
                                            <Input data-testid={`leasing-residual-${i}`} type="number" value={lo.residual_value} onChange={(e) => updateLeasing(i, "residual_value", e.target.value)} className="mt-1" /></div>
                                        <div><Label className="text-[10px] uppercase tracking-wider">Månadskostnad (SEK)</Label>
                                            <Input data-testid={`leasing-monthly-${i}`} type="number" value={lo.monthly_cost} onChange={(e) => updateLeasing(i, "monthly_cost", e.target.value)} className="mt-1" /></div>
                                    </div>
                                </div>
                            ))}
                            <Button type="button" variant="outline" size="sm" onClick={addLeasing}><Plus size={14} className="mr-1" /> Lägg till leasingförslag</Button>
                        </div>

                        <Separator />

                        <div><Label className="text-xs uppercase tracking-wider">Anteckningar</Label>
                            <Textarea data-testid="offer-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>

                        <div className="flex justify-between items-center pt-2 border-t">
                            <span className="font-medium">Totalt: {total.toLocaleString()} SEK <span className="text-xs text-neutral-400">({form.vat_mode === "ink" ? "ink moms" : "ex moms"})</span></span>
                            <Button data-testid="submit-offer-btn" type="submit" className="bg-black text-white">Skapa offert</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
